/**
 * Group runs joined to their participants (GROUP-ORIENTATION-PLAN Phase 5),
 * from `visible_group_runs` + `visible_group_run_participants` (both
 * admin-gated, subscribed in the cohort batch — they landed in the same
 * publish, so their availability is identical).
 */
import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import { tables as minetwinTables } from '@sdk/minetwin';
import { toMillis } from '@/lib/toMillis';
import type { GroupMemberState, GroupRunStatusTag } from '@/lib/groupRunEntity';

export type GroupRunRowData = {
  groupRunId: bigint;
  cohortAssignmentId: bigint;
  projectId: string;
  name: string;
  sessionId: bigint;
  leaderEmail: string;
  modeTag: string;
  statusTag: GroupRunStatusTag;
  currentStepId: string | null;
  pausedByEmail: string | null;
  createdAtMillis: number;
  /** Phase 10 run-row settings, editable in the Lobby via update_group_run. */
  allowLateJoin: boolean;
  autoStallSeconds: number;
  members: GroupMemberState[];
};

export function useGroupRuns(projectId?: string): {
  runs: GroupRunRowData[];
  isReady: boolean;
} {
  const [runRows, runsReady] = useTable('minetwin', minetwinTables.visible_group_runs);
  const [memberRows] = useTable(
    'minetwin',
    minetwinTables.visible_group_run_participants,
  );

  const runs = useMemo<GroupRunRowData[]>(() => {
    const membersByRun = new Map<string, GroupMemberState[]>();
    for (const m of memberRows) {
      const key = String(m.groupRunId);
      const list = membersByRun.get(key) ?? [];
      list.push({
        assignmentId: m.assignmentId,
        email: m.email,
        displayName: m.displayName,
        connected: m.connected,
        pendingChecks: m.pendingChecks,
      });
      membersByRun.set(key, list);
    }
    for (const list of membersByRun.values()) {
      list.sort((a, b) => a.email.localeCompare(b.email));
    }

    return runRows
      .filter((r) => !projectId || r.projectId === projectId)
      .map((r) => ({
        groupRunId: r.groupRunId,
        cohortAssignmentId: r.cohortAssignmentId,
        projectId: r.projectId,
        name: r.name,
        sessionId: r.sessionId,
        leaderEmail: r.leaderEmail,
        modeTag: r.mode.tag,
        statusTag: r.status.tag as GroupRunStatusTag,
        currentStepId: r.currentStepId ?? null,
        pausedByEmail: r.pausedByEmail ?? null,
        createdAtMillis: toMillis(r.createdAt),
        allowLateJoin: Boolean(r.allowLateJoin),
        autoStallSeconds: Number(r.autoStallSeconds ?? 0),
        members: membersByRun.get(String(r.groupRunId)) ?? [],
      }))
      .sort((a, b) => b.createdAtMillis - a.createdAtMillis);
  }, [runRows, memberRows, projectId]);

  return { runs, isReady: runsReady };
}
